import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { attachAuthRefresh } from '../lib/apiClient';
attachAuthRefresh(axios);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip auth probe on fully public routes so anonymous customers don't see
    // 401 noise in the console (e.g., /pay/:invoiceId, /w9/complete/:token,
    // /onboarding/:token).
    const path = window.location.pathname;
    const isPublicRoute =
      path.startsWith('/pay/') ||
      path.startsWith('/w9/complete/') ||
      path.startsWith('/onboarding/') ||
      path.startsWith('/demo-invite/') ||
      path.startsWith('/team/accept/');
    if (isPublicRoute) {
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      return { success: true, mustChangePassword: !!data.must_change_password };
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMsg };
    }
  };

  const register = async (email, password, name, role, company_id) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role, company_id }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMsg };
    }
  };

  const companySignup = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/auth/company-signup`, payload, { withCredentials: true });
      setUser(data.user);
      return { success: true, data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'carrier_already_registered') {
        return { success: false, error: detail.message, code: detail.code, identifier: detail.identifier };
      }
      const errorMsg = formatApiErrorDetail(detail) || error.message;
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Purge offline cache so the next user on this device cannot see the
    // previous user's loads, drivers, or queued operations. Best-effort —
    // never block logout on an IndexedDB error.
    try {
      const { clearAll } = await import('../db/offlineStore');
      await clearAll();
    } catch { /* IDB unavailable — non-fatal */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, companySignup, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};